import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  Lock,
  CreditCard,
  Boxes,
  Recycle,
  Layers,
  MapPin,
  CircleCheck,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison + 3 engagements (layout 2-cols) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Acheminement express
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de cartons à {city.name} : acheminement express
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Ne gaspillez plus vos heures dans les files d&apos;attente des
            enseignes de bricolage ou à saturer l&apos;espace de votre
            véhicule avec des formats encombrants. Nous prenons en main le
            transfert direct de vos fournitures jusqu&apos;au cœur de
            {" "}{city.name}.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Logistique intégrée propriétaire",
              body: "Aucun transporteur externe n'intervient dans le flux. Notre propre flotte francilienne charge et décharge vos fournitures, garantissant le respect scrupuleux des plages horaires et un soin chirurgical lors des manipulations.",
              Icon: Truck,
            },
            {
              title: "Garantie structurelle",
              body: "Protégés des poussières urbaines et des variations hygrométriques au sein de nos fourgons scellés, vos modules conservent leur rigidité nominale. Vous ouvrez des colis intacts, prêts pour une mise en volume immédiate.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        {/* Sub-block : 3 engagements numérotés */}
        <div className="mt-12">
          <p className="text-[13px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
            Nos trois engagements majeurs
          </p>
          <ol className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                n: "01",
                title: "Disponibilité absolue du stock",
                body: "Nos entrepôts régionaux maintiennent un inventaire permanent sur toutes les dimensions. Votre commande est validée et préparée en temps réel pour éviter tout blocage de votre planning.",
                Icon: MapPin,
              },
              {
                n: "02",
                title: "Robustesse certifiée",
                body: "Chaque référence fait l'objet de tests de compression verticale. Nos cartons supportent le gerbage intensif dans les utilitaires sans jamais s'affaisser, protégeant les couches inférieures de toute pression.",
                Icon: ShieldCheck,
              },
              {
                n: "03",
                title: "Filière éco-responsable",
                body: "L'intégralité de notre catalogue provient de la revalorisation de matières végétales. Nos produits sont 100 % biodégradables et réutilisables, limitant l'impact carbone de votre déménagement.",
                Icon: Recycle,
              },
            ].map(({ n, title, body, Icon }) => (
              <li
                key={n}
                className="relative bg-white rounded-2xl border border-[#cfdbe6] p-5"
              >
                <div className="absolute -top-3 right-4 inline-flex items-center justify-center h-8 px-3 rounded-full bg-[#0f1d2b] text-white font-display font-extrabold text-[12px] tracking-[0.1em]">
                  N° {n}
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                    {title}
                  </h3>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Logistique digitale : 3 étapes (cards verticales) ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Devis express
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Logistique digitale : obtenez votre devis en quelques clics
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que votre temps est précieux, nous avons numérisé notre
            processus d&apos;estimation. Configurer vos besoins ne vous
            prendra que quelques instants.
          </p>
        </div>

        <ol className="mt-10 space-y-4 max-w-3xl">
          {[
            {
              n: "01",
              title: "Sélectionnez vos modules",
              body: "Sélectionnez vos modules (cartons standards, penderies, boîtes à archives) ou optez pour l'un de nos kits volumétriques pré-calibrés.",
              Icon: Boxes,
            },
            {
              n: "02",
              title: "Renseignez vos coordonnées",
              body: `Renseignez vos coordonnées géographiques à ${city.name} et validez votre panier d'achat.`,
              Icon: MapPin,
            },
            {
              n: "03",
              title: "Recevez votre devis",
              body: "Notre algorithme calcule instantanément les modalités d'acheminement optimales pour générer votre bon de commande clair, transparent et détaillé, sans la moindre ligne de frais cachés.",
              Icon: CircleCheck,
            },
          ].map(({ n, title, body, Icon }) => (
            <li
              key={n}
              className="flex items-start gap-4 bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex flex-col items-center shrink-0">
                <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white font-display font-extrabold text-[15px]">
                  {n}
                </span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Icon className="h-5 w-5 text-[#237cb5]" strokeWidth={2.4} />
                  <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                    {title}
                  </h3>
                </div>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-puteaux-cta-shop"
          >
            Configurer mon devis
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/packs-cartons"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-puteaux-cta-packs"
          >
            <Layers className="h-4 w-4" strokeWidth={2.6} />
            Kits volumétriques
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Paiement sécurisé (split 2 cards horizontales) ======= */}
    <section className="py-14 md:py-20 bg-[#0f1d2b] text-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] uppercase tracking-[0.18em] font-bold text-white/80 mb-4">
            <Lock className="h-3.5 w-3.5" strokeWidth={2.6} />
            Sécurité de niveau bancaire
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-white">
            Transactions cryptées : un protocole de paiement sécurisé à
            100&nbsp;%
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-white/80 leading-relaxed">
            La sécurité ne s&apos;arrête pas à la solidité de nos cartons ;
            elle englobe également la protection de vos données
            financières. Lors de la validation de votre commande sur notre
            boutique, vous bénéficiez d&apos;un environnement de
            transaction hautement protégé.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Technologie SSL / TLS avancée",
              body: "Toutes les informations qui transitent via notre interface font l'objet d'un cryptage lourd, rendant l'interception de vos coordonnées bancaires techniquement impossible.",
              Icon: Lock,
            },
            {
              title: "Authentification forte (3D Secure)",
              body: "Chaque paiement fait l'objet d'une double vérification en liaison directe avec votre établissement bancaire (validation par empreinte ou code unique sur votre smartphone). Vous passez commande en toute sérénité.",
              Icon: CreditCard,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/15 p-5 md:p-6"
            >
              <div className="flex items-start gap-3">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#FF7A00] text-white shrink-0">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15px] text-white">
                    {title}
                  </h3>
                  <p className="mt-2 text-[13.5px] text-white/70 leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-puteaux-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
